/**
 * Pure logic for the all-projects Pipelines board: which lane a build is in,
 * whether a failure has since been fixed, and how builds fold into groups.
 * The store's computeds are thin wrappers over these, so every rule here is
 * unit-tested without MobX or a DOM.
 */
import type {
  BuildBoardBuild,
  BuildBoardCategory,
  BuildBoardClientRef,
  BuildBoardProject,
} from "./buildBoard.types";

/** The four lanes the board is about. `failed` is split out of Azure's `completed`. */
export type BoardLane = "building" | "queued" | "failed" | "completed";
export type LaneFilter = "all" | BoardLane;
export type CategoryFilter = "all" | BuildBoardCategory;
export type GroupBy = "client" | "project";
export type BuildBoardWindow = 6 | 24 | 72 | 168;

export const WINDOW_OPTIONS: ReadonlyArray<{ value: BuildBoardWindow; label: string }> = [
  { value: 6, label: "6 hours" },
  { value: 24, label: "24 hours" },
  { value: 72, label: "3 days" },
  { value: 168, label: "7 days" },
];

/**
 * Results that put a finished build in the Failed lane. A CANCELED build is not
 * here: somebody stopped it, nothing broke, and counting it would make a
 * deliberate cancel read as a red board. (The overview's attention flag counts
 * it — that flag asks "did the newest build succeed?", a different question.)
 */
const FAILED_RESULTS: ReadonlySet<string> = new Set(["failed", "partiallysucceeded"]);

export function laneOf(build: BuildBoardBuild): BoardLane {
  if (build.lane === "building") return "building";
  if (build.lane === "queued") return "queued";
  return build.result !== null && FAILED_RESULTS.has(build.result) ? "failed" : "completed";
}

/** Lane order inside a group: what needs eyes first. */
const LANE_RANK: Record<BoardLane, number> = { building: 0, queued: 1, failed: 2, completed: 3 };

const pipelineKey = (b: BuildBoardBuild): string =>
  `${b.connectionId}|${b.projectId}|${b.definitionId ?? b.definitionName ?? ""}|${b.sourceBranch ?? ""}`;

/**
 * For every failed build, the newer SUCCESSFUL build of the same pipeline on
 * the same branch that fixed it — or nothing, meaning it is still failing as
 * far as the window shows. Only a success fixes a failure; a later cancel or
 * a later failure does not. A failure on `feature/x` is not fixed by `master`.
 *
 * Returns build key → the fixing build.
 */
export function fixedBy(builds: readonly BuildBoardBuild[]): Map<string, BuildBoardBuild> {
  const successes = new Map<string, BuildBoardBuild[]>();
  for (const b of builds) {
    if (b.lane === "completed" && b.result === "succeeded" && b.finishTime) {
      const k = pipelineKey(b);
      const list = successes.get(k) ?? [];
      list.push(b);
      successes.set(k, list);
    }
  }
  const out = new Map<string, BuildBoardBuild>();
  for (const b of builds) {
    if (laneOf(b) !== "failed" || !b.finishTime) continue;
    const later = (successes.get(pipelineKey(b)) ?? [])
      .filter((s) => (s.finishTime ?? "") > (b.finishTime ?? ""))
      .sort((x, y) => (x.finishTime ?? "").localeCompare(y.finishTime ?? ""));
    if (later.length > 0) out.set(b.key, later[0]);
  }
  return out;
}

export interface LaneCounts {
  building: number;
  queued: number;
  /** Failures in the window that no later success has fixed. */
  failing: number;
  /** Every failure in the window, fixed or not. */
  failed: number;
  completed: number;
}

export function countLanes(
  builds: readonly BuildBoardBuild[],
  fixes: ReadonlyMap<string, BuildBoardBuild>,
): LaneCounts {
  const c: LaneCounts = { building: 0, queued: 0, failing: 0, failed: 0, completed: 0 };
  for (const b of builds) {
    const lane = laneOf(b);
    if (lane === "failed") {
      c.failed += 1;
      if (!fixes.has(b.key)) c.failing += 1;
    } else {
      c[lane] += 1;
    }
  }
  return c;
}

/**
 * What kind of group a set of builds forms.
 *
 *   client    one client's builds, across every project it is linked to
 *   shared    a client project several clients share, for the pipelines no
 *             link claims — HQ does not guess whose they are
 *   internal  IntraSync's own project: no client linked
 */
export type GroupKind = "client" | "shared" | "internal";

export interface BuildGroup {
  key: string;
  kind: GroupKind;
  label: string;
  /** Project mode: the clients linked to the project. Client mode: the projects the builds came from. */
  context: string[];
  builds: BuildBoardBuild[];
  counts: LaneCounts;
  /** Newest queue/start/finish time among the group's builds. */
  lastActivity: string;
}

export interface GroupInput {
  builds: readonly BuildBoardBuild[];
  projects: readonly BuildBoardProject[];
  clients: readonly BuildBoardClientRef[];
  fixes: ReadonlyMap<string, BuildBoardBuild>;
  groupBy: GroupBy;
}

const activityOf = (b: BuildBoardBuild): string => b.finishTime ?? b.startTime ?? b.queueTime ?? "";

/** Building, then queued, then still-failing, then everything finished newest first. */
export function compareBuilds(
  a: BuildBoardBuild,
  b: BuildBoardBuild,
  fixes: ReadonlyMap<string, BuildBoardBuild>,
): number {
  const rank = (x: BuildBoardBuild): number => {
    const lane = laneOf(x);
    // A fixed failure is history now — it sorts with the finished builds.
    if (lane === "failed" && fixes.has(x.key)) return LANE_RANK.completed;
    return LANE_RANK[lane];
  };
  const byLane = rank(a) - rank(b);
  if (byLane !== 0) return byLane;
  return activityOf(b).localeCompare(activityOf(a));
}

export function groupBuilds({ builds, projects, clients, fixes, groupBy }: GroupInput): BuildGroup[] {
  const projectByKey = new Map(projects.map((p) => [p.key, p]));
  const clientById = new Map(clients.map((c) => [c.id, c]));
  const acc = new Map<string, { kind: GroupKind; label: string; context: Set<string>; builds: BuildBoardBuild[] }>();

  for (const b of builds) {
    let key: string;
    let kind: GroupKind;
    let label: string;
    const context: string[] = [];
    if (groupBy === "client" && b.clientId !== null) {
      key = `client:${b.clientId}`;
      kind = "client";
      label = clientById.get(b.clientId)?.name ?? b.clientId;
      context.push(b.projectName);
    } else {
      key = `project:${b.projectKey}`;
      kind = b.category === "internal" ? "internal" : groupBy === "client" ? "shared" : "client";
      label = b.projectName;
      for (const c of projectByKey.get(b.projectKey)?.clients ?? []) context.push(c.name);
    }
    let g = acc.get(key);
    if (!g) {
      g = { kind, label, context: new Set(), builds: [] };
      acc.set(key, g);
    }
    for (const c of context) g.context.add(c);
    g.builds.push(b);
  }

  const groups: BuildGroup[] = [];
  for (const [key, g] of acc) {
    const sorted = [...g.builds].sort((x, y) => compareBuilds(x, y, fixes));
    groups.push({
      key,
      kind: g.kind,
      label: g.label,
      context: [...g.context].sort((x, y) => x.localeCompare(y)),
      builds: sorted,
      counts: countLanes(sorted, fixes),
      lastActivity: sorted.reduce((max, b) => (activityOf(b) > max ? activityOf(b) : max), ""),
    });
  }
  return groups.sort(compareGroups);
}

/**
 * Groups that need eyes first: anything building, then queued, then still
 * failing, then the most recently active. Ties fall back to the name so the
 * order does not shuffle between refreshes.
 */
export function compareGroups(a: BuildGroup, b: BuildGroup): number {
  const urgency = (g: BuildGroup): number =>
    g.counts.building > 0 ? 0 : g.counts.queued > 0 ? 1 : g.counts.failing > 0 ? 2 : 3;
  return (
    urgency(a) - urgency(b) ||
    b.lastActivity.localeCompare(a.lastActivity) ||
    a.label.localeCompare(b.label)
  );
}

/** The section a group is shown under: clients' work, or IntraSync's own. */
export const sectionOf = (g: BuildGroup): BuildBoardCategory => (g.kind === "internal" ? "internal" : "client");

export function matchesLane(
  b: BuildBoardBuild,
  filter: LaneFilter,
  fixes: ReadonlyMap<string, BuildBoardBuild>,
): boolean {
  if (filter === "all") return true;
  const lane = laneOf(b);
  // The Failed lane shows what is still failing; a fixed failure is history.
  if (filter === "failed") return lane === "failed" && !fixes.has(b.key);
  return lane === filter;
}

export function matchesSearch(
  b: BuildBoardBuild,
  query: string,
  clientName: string | null,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [b.definitionName, b.projectName, b.sourceBranch, b.requestedFor, b.buildNumber, clientName]
    .filter((v): v is string => !!v)
    .some((v) => v.toLowerCase().includes(q));
}

/** `refs/heads/master` → `master`; a pull-request ref is shown as `PR 123`. */
export function branchLabel(ref: string | null | undefined): string {
  if (!ref) return "—";
  const pr = /^refs\/pull\/(\d+)\//.exec(ref);
  if (pr) return `PR ${pr[1]}`;
  return ref.replace(/^refs\/heads\//, "");
}

/** "4m 12s", "1h 03m" — or null when either end is missing. */
export function duration(fromIso: string | null, toIso: string | null | number): string | null {
  if (!fromIso || toIso === null) return null;
  const from = Date.parse(fromIso);
  const to = typeof toIso === "number" ? toIso : Date.parse(toIso);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return null;
  const s = Math.round((to - from) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
}
